import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Opportunity : famille "piste commerciale".
 *
 * Piste ou demande entrante à qualifier, suivie par étapes jusqu'au gain, à la
 * perte ou à l'abandon, avec sa source et sa valeur estimée. Construite sur le
 * modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("lead web", "devis
 * atelier") : il connaît la famille générique `opportunity`. Le discriminant
 * `type` porte le genre déclaré par l'app ; l'avancement du cycle (`stage`) et
 * le motif de perte (`lostReason`) sont des états métier à part, chaînes libres
 * pilotées par l'app.
 *
 * Les liens vers les autres familles (contact, order, campaign, message,
 * agreement, worker) sont de simples ids string, jamais un import d'un autre
 * schéma. `contactId` reste OPTIONNEL : une demande entrante peut naître avant
 * d'être rattachée à un contact (elle est justement "à qualifier").
 *
 * Accès : v0 org-wide (même doctrine que asset), rien de sensible signalé.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille opportunity (hors champs standard du socle). */
export const opportunityCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "lead", "devis"). */
  type: z.string().min(1),
  /** Prospect concerné (référence `contact`). Optionnel : la piste peut précéder le contact. */
  contactId: z.string().min(1).optional(),
  /** Étape courante du cycle, chaîne libre pilotée par l'app (ex. "nouveau", "qualifie", "gagne", "perdu"). Optionnel. */
  stage: z.string().min(1).optional(),
  /** Valeur estimée de l'affaire (montant). Optionnel. */
  expectedValue: z.number().optional(),
  /** Probabilité de gain (ex. 0.4 ou 40 selon la convention de l'app). Optionnel. */
  probability: z.number().optional(),
  /** Canal d'origine de la piste (ex. "web", "salon", "recommandation"). Optionnel. */
  source: z.string().min(1).optional(),
  /** Date de clôture prévue. Optionnel. */
  expectedCloseAt: z.date().optional(),
  /** Commercial responsable (référence `worker`). Optionnel. */
  ownerWorkerId: z.string().min(1).optional(),
  /** Motif de perte ou d'abandon, chaîne libre. Optionnel. */
  lostReason: z.string().min(1).optional(),
  /** Commande issue du gain (référence `order`). Optionnel. */
  orderId: z.string().min(1).optional(),
  /** Campagne d'origine (référence `campaign`). Optionnel. */
  campaignId: z.string().min(1).optional(),
  /** Message entrant d'origine (référence `message`). Optionnel. */
  messageId: z.string().min(1).optional(),
  /** Accord ou contrat rattaché (référence `agreement`). Optionnel. */
  agreementId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille opportunity (champs standard + champs métier). */
export const OpportunitySchema = z.object({
  ...baseEntityFields,
  ...opportunityCoreFields,
});

/** Type d'une opportunity. */
export type Opportunity = z.infer<typeof OpportunitySchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const OpportunityCreateInputSchema = z.object({
  ...opportunityCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une opportunity. */
export type OpportunityCreateInput = z.infer<typeof OpportunityCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const OpportunityUpdateInputSchema = z
  .object({ ...opportunityCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'une opportunity. */
export type OpportunityUpdateInput = z.infer<typeof OpportunityUpdateInputSchema>;
